import {
  Component,
  HostListener,
  Input,
  OnChanges,
  forwardRef,
  isDevMode,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SuiTooltipDirective } from '@sensika/sui/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { NgpToggleGroup, NgpToggleGroupItem } from 'ng-primitives/toggle-group';

import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '@sensika/sui/form-field';
import { SuiFormControlBase } from '@sensika/sui/shared';
import type { SuiButtonSize } from '@sensika/sui/button';

export interface SuiButtonGroupOption {
  /** Visible text. Omit for an icon-only segment (then `ariaLabel` is required). */
  label?: string;
  /** The value bound/emitted for this segment. Any type (see `compareWith`). */
  value: any;
  /** Optional leading icon. */
  icon?: IconProp;
  /** Per-option disable (composes with the group-level `disabled`). */
  disabled?: boolean;
  /** Accessible label — required for icon-only segments. */
  ariaLabel?: string;
  /** Hover / focus tooltip, e.g. the meaning of a terse preset label (WAP-1763). */
  tooltip?: string;
}

/**
 * Segmented control — "pick one (single) or many (multiple) from N buttons".
 *
 * A value-holding form control built on `NgpToggleGroup` (same primitive as
 * `sui-toggle`), so it gets CVA, roving-tabindex keyboard nav, and correct
 * selection semantics for free. Single-select is **mandatory**: once a segment
 * is chosen it can't be clicked off (`allowDeselection = false`); multiple-select
 * toggles each segment independently.
 *
 * The selected segment reuses the button "pressed" info-tint look, driven by the
 * primitive's `data-selected` — consumers never wire `[pressed]` per segment.
 */
@Component({
  selector: 'sui-button-group',
  standalone: true,
  imports: [
    NgpToggleGroup,
    NgpToggleGroupItem,
    FaIconComponent,
    SuiTooltipDirective,
  ],
  templateUrl: './sui-button-group.component.html',
  styleUrl: './sui-button-group.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiButtonGroupComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiButtonGroupComponent),
    },
  ],
})
export class SuiButtonGroupComponent
  extends SuiFormControlBase<any>
  implements SuiFormFieldControl, OnChanges
{
  private static _idCounter = 0;
  private readonly _uid = `sui-button-group-${++SuiButtonGroupComponent._idCounter}`;
  private _focused = false;

  readonly controlType = 'button-group';

  @Input() options: SuiButtonGroupOption[] = [];
  @Input() multiple = false;
  @Input() size: SuiButtonSize = 'md';
  @Input() id = '';
  /** Value equality for matching options — override for object values. */
  @Input() compareWith: (a: any, b: any) => boolean = (a, b) => a === b;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    const v = this.value();
    return this.multiple ? !(Array.isArray(v) && v.length) : v == null;
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  /** Stable string key crossing the primitive boundary (typed for `string`). */
  key(index: number): string {
    return String(index);
  }

  /** Current selection as the primitive's `string[]` value. */
  get groupValue(): string[] {
    const opts = this.options ?? [];
    if (this.multiple) {
      const vals = Array.isArray(this.value()) ? (this.value() as any[]) : [];
      return opts
        .map((o, i) => ({ o, i }))
        .filter(({ o }) => vals.some((v) => this.compareWith(v, o.value)))
        .map(({ i }) => this.key(i));
    }
    const v = this.value();
    if (v == null) {
      return [];
    }
    const i = opts.findIndex((o) => this.compareWith(v, o.value));
    return i >= 0 ? [this.key(i)] : [];
  }

  effectiveDisabled(option: SuiButtonGroupOption): boolean {
    return !!option.disabled || this.isDisabled();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  onValueChange(keys: string[]): void {
    const opts = this.options ?? [];
    if (this.multiple) {
      const vals = keys
        .map((k) => opts[Number(k)]?.value)
        .filter((v) => v !== undefined);
      this.setValue(vals);
    } else {
      const opt = opts[Number(keys[0])];
      this.setValue(opt ? opt.value : null);
    }
  }

  ngOnChanges(): void {
    if (isDevMode()) {
      const bad = (this.options ?? []).find((o) => !o.label && !o.ariaLabel);
      if (bad) {
        console.warn(
          '[sui-button-group] an icon-only option (no `label`) is missing an `ariaLabel` - it is invisible to screen readers.',
        );
      }
    }
  }
}
